import React, {FunctionComponent, useCallback, useState} from 'react';
import {Form} from 'react-final-form';
import {
    MIN_PASSWORD_LENGTH,
    validateComplexity,
    validateEmail,
    validateMinLength,
    validateRequired
} from '../commonOpenSite/validation/basicValidators';
import {composeFieldValidators} from '../commonOpenSite/validation/utils';
import {Subject} from 'rxjs';
import {FieldValidator} from '../commonOpenSite/validation/validator.interface';
import {debounceTime, map, publish, refCount, switchMap, take} from 'rxjs/operators';
import {ajaxGet} from 'rxjs/internal-compatibility';
import {SignupField} from './signupField/signupField.component';
import {HumanValidatedMethod} from "../commonOpenSite/captcha/captchaV3Hook";
import {Captcha} from "../commonOpenSite/captcha/captcha.component";
import {KlTos} from "../klTos";
import {useIsMobile} from "../commonOpenSite/hooks/useIsMobile.hook";
import axios from "axios";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./openSiteSignup.component.scss');

const emptySignInForm: SignInForm = {
    email: '',
    password: ''
};
const emptySignupForm: SignupForm = {
    fullName: '',
    email: '',
    password: '',
    emailAgreement: false,
    privacyRead: false
};

const captchaActionNameLogin = 'creator_login';
const captchaActionNameSignup = 'creator_signup';

interface SignupProps {
    extraLogo?: string;
    listId?: string;
    influencerId?: string;
    nextLocation: string;
}

interface SignupForm {
    fullName: string;
    email: string;
    password: string;
    emailAgreement: boolean;
    privacyRead: boolean;
}

interface SignInForm {
    email: string;
    password: string;
}

//TODO Alex - use custom hook later
declare const analytics: {
    track: (eventName: string, params?: Record<string, string | boolean | null>) => Promise<void>;
};

const errorToMessage = (errors: { [key: string]: boolean }): string => {
    if (!errors) return '';
    if (errors.required) return 'Required';
    if (errors.email) return 'Please enter a valid email address';
    if (errors.alreadyExists) return 'Email address already exists';
    if (errors.minLength) return `Please enter at least ${MIN_PASSWORD_LENGTH} characters`;
    if (errors.complexity) return 'Password must contain at least one special character - e.g.: $, #, @, *';
    return 'Error in field';
};

const requiredEmailValidator = composeFieldValidators<string>([validateRequired, validateEmail]);
const emailValidationValue$ = new Subject<string>();
const emailValid$ = emailValidationValue$.pipe(
    debounceTime(200),
    switchMap(email => ajaxGet(`/api/2.1/Register/exists?username=${email}`)),
    map(response => response.response as boolean),
    map(emailExists => emailExists ? {
        alreadyExists: true
    } : undefined),
    publish(),
    refCount());

const accountExistsValidator: FieldValidator<string> = (email: string) => {
    if (!email) return Promise.resolve(undefined);
    return new Promise(resolve => {
        emailValid$.pipe(take(1))
            .subscribe(errors => {
                resolve(errors);
            });
        emailValidationValue$.next(email);
    });
};


export const OpenSiteSignupComponent: FunctionComponent<SignupProps> = (props) => {
    const {extraLogo, listId, influencerId, nextLocation} = props;
    const [isSignin, setSignin] = useState(false);
    const [signinSubmitDisabled, setSigninSubmitDisabled] = useState(false);
    const [signupSubmitDisabled, setSignupSubmitDisabled] = useState(false);
    const [showErrorSignUp, setShowErrorSignUp] = useState(true);
    const [showErrorSignIn, setShowErrorSignIn] = useState(true);

    const onSuccess = useCallback( () => {
        location.href = nextLocation;
    },[nextLocation]);

    const onSignupSuccess = useCallback((values: SignupForm) => {
        if(typeof analytics !== 'undefined'){
            analytics.track(`Creator Signed Up`, {
                category: 'Creator Signup',
                label: document.title,
                creator_name: values.fullName,
                creator_email: values.email
            });
        }
        onSuccess();
    },[onSuccess]);
    const isMobile = useIsMobile();
    const [callback, setCallback] = useState<HumanValidatedMethod | null>(null);
    const handleRunCaptchaChanged = useCallback((newRunCaptcha: HumanValidatedMethod) => {
        setCallback(() => newRunCaptcha);
    }, []);

    const createAccount = useCallback((values: SignupForm) => {
        if (!callback) return;
        return callback( (v3Token, captchaActionNameSignup, v2Token) => {
            return axios.post(`/api/2.1/Register/createCreatorAccount`, {fullName: values.fullName, email: values.email, password: values.password,
                tokenV3: v3Token, action_name: captchaActionNameSignup, tokenV2: v2Token, emailAgreement: values.emailAgreement}).then(() => {return;});
        });
    }, [callback]);

    const signIn = useCallback((values: SignInForm) => {
        if (!callback) return;
        return callback( (v3Token, captchaAction, v2Token) => {
            return axios.post(`/api/2.1/Register/login`, {email: values.email, password: values.password, tokenV3: v3Token,
                action_name: captchaActionNameLogin, tokenV2: v2Token}).then(() => {return;});
        });
    }, [callback]);

        return (
            <div className={styles.klearProfilesModal} id="signUpModal" tabIndex={-1} role="dialog"
                 aria-labelledby="myModalLabel">
                <div className={`${styles.creatorSignUpModal} ${isMobile ? '' : styles.maxWidth} flexModal`}>
                    <div className={styles.modalTop}>
                        {extraLogo ?
                            <>
                                <div className={styles.subTitle}>Sign up to klear to collaborate with us</div>
                            </> :
                            <>
                                <div className={styles.title}>Klear Creators</div>

                                <div className={styles.subTitle}> Sign Up to get offers from leading brands</div>
                            </>
                        }
                        {isSignin ?
                            <div className={styles.smallTitle}>
                                New User? <a href={'#'} onClick={() => setSignin(false)}>Sign Up</a>
                            </div> :
                            <div className={styles.smallTitle}>
                                Already A User? <a href={'#'} onClick={() => setSignin(true)} aria-label={'Creator Sign In'}>Sign
                                In</a>
                            </div>
                        }
                    </div>

                    {isSignin ?
                        <Form initialValues={emptySignInForm}
                              onSubmit={(values: SignInForm) => signIn(values)}>
                            {({handleSubmit, submitting, hasValidationErrors, validating, hasSubmitErrors, submitErrors}) =>
                                <form className={styles.validateForm} onSubmit={handleSubmit}>
                                    <fieldset className={styles.fieldsetContainer}>
                                        <div className={`${styles.inputsContainer} ${styles.modalContainer}`}>
                                            <div className="inputBox">
                                                <label className={styles.signupInputLabel}>Email:</label>
                                                <SignupField errorToMessage={errorToMessage} validate={requiredEmailValidator}
                                                             name={"email"} placeholder={"Email"}
                                                             type={"email"} tabIndex={1} id={"suEmail"}/>
                                                <label className={styles.signupInputLabel}>Password:</label>
                                                <SignupField errorToMessage={errorToMessage} validate={validateRequired}
                                                             name={"password"} tabIndex={2} id={"suPassword"}
                                                             type={"password"} placeholder={"Enter Password"}/>
                                            </div>
                                            <a className={styles.forgotLink} href={'/forgot-password'}>Forgot Your Password?</a>
                                        </div>
                                        {showErrorSignIn && hasSubmitErrors ?
                                            <div className={styles.errorRed}>{submitErrors.error}</div>
                                            : null}
                                        <Captcha onRunCaptchaChanged={handleRunCaptchaChanged} onSuccess={onSuccess}
                                                 actionName={captchaActionNameLogin}
                                                 disableSubmit={setSigninSubmitDisabled} showError={setShowErrorSignIn} className={styles.recaptcha}/>
                                        <div className={`modalSection ${styles.signupButton}`}>
                                            <input
                                                disabled={hasValidationErrors || validating || submitting || signinSubmitDisabled}
                                                className={styles.butt2020}
                                                value="Sign In"
                                                aria-label={"Creator Sign In Button"}
                                                tabIndex={3} type="submit"/>
                                        </div>
                                    </fieldset>
                                </form>}
                        </Form> :
                        <Form initialValues={emptySignupForm}
                              onSubmit={(values: SignupForm) => createAccount(values)}>
                            {({handleSubmit, values, submitting, ...formProps}) => <form className={styles.validateForm}
                                                                                           id={'creatorSignupForm'}
                                                                                           onSubmit={handleSubmit}>
                                <fieldset className={styles.fieldsetContainer}>
                                    <div className={`${styles.inputsContainer} ${styles.modalContainer}`}>
                                        <div className="inputBox">
                                            <label className={styles.signupInputLabel}>Full Name:</label>
                                            <SignupField errorToMessage={errorToMessage} validate={validateRequired} id={"suFullName"}
                                                         name={"fullName"} placeholder={"Full Name"} tabIndex={1}/>
                                            <label className={styles.signupInputLabel}>Email:</label>
                                            <SignupField errorToMessage={errorToMessage} id={"suEmail"}
                                                         validate={composeFieldValidators([validateRequired, validateEmail, accountExistsValidator])}
                                                         name={"email"} type={"email"} placeholder={"Email"} tabIndex={2}/>
                                            <label className={styles.signupInputLabel}>Password:</label>
                                            <SignupField errorToMessage={errorToMessage} tabIndex={3} placeholder={"Choose Your Password"}
                                                         validate={composeFieldValidators([
                                                             validateRequired,
                                                             validateMinLength, validateComplexity
                                                         ])}
                                                         name={"password"} type={"password"} id={"suPassword"}/>
                                        </div>
                                        <KlTos
                                            emailAgreementFieldName={`emailAgreement`}
                                            privacyReadFieldName={`privacyRead`}
                                            validatePrivacy
                                        />
                                        {showErrorSignUp && formProps.hasSubmitErrors ?
                                            <div className={styles.errorRed}>{formProps.submitErrors.error}</div>
                                            : null}
                                    </div>
                                    <Captcha onRunCaptchaChanged={handleRunCaptchaChanged} onSuccess={onSuccess}
                                             actionName={captchaActionNameSignup}
                                             disableSubmit={setSignupSubmitDisabled} showError={setShowErrorSignUp} className={styles.recaptcha}/>
                                    <div className={styles.signupButton}>
                                        <input
                                            disabled={formProps.hasValidationErrors || formProps.validating || submitting || signupSubmitDisabled}
                                            className={styles.butt2020}
                                            value="Sign Up"
                                            tabIndex={5} type="submit"/>
                                    </div>
                                </fieldset>
                            </form>}
                        </Form>
                    }
                </div>
            </div>
        );
};
